import { css } from "@emotion/react";
import CloseIcon from "@mui/icons-material/Close";
import { Dialog, DialogContent, IconButton, useTheme } from "@mui/material";
import { useState } from "react";
import { match } from "ts-pattern";

import Loading from "~/components/me/bento/Loading";
import RetriableError from "~/components/me/bento/RetriableError";
import BentoItemContainer, {
  PC_BENTO_HEIGHT,
  PC_BENTO_WIDTH,
  SP_BENTO_HEIGHT,
  SP_BENTO_WIDTH,
} from "~/components/me/bento/containers/BentoItemContainer";
import { PC_BENTO_ITEM_GAP_SIZE } from "~/components/me/bento/containers/BentoItemRow";
import { useMeFussyAttestationContent } from "~/components/me/bento/lib";
import { mediaQueryIsDesktop, mediaQueryIsMobile } from "~/lib/client/design";
import useIsSpMode from "~/lib/client/hooks/useIsSpMode";
import { MeFussyAttestationContentQuery, Sdk } from "~/lib/graphql/client";
import { formatDate } from "~/lib/graphql/graphqlClient";

export const FussyAttestationCardHolder = ({ client }: { client: Sdk }) => {
  const content = useMeFussyAttestationContent(client);

  return match(content)
    .with({ status: "pending" }, () => <Loading />)
    .with({ status: "error" }, (matched) => (
      <RetriableError reload={matched.refetch} />
    ))
    .with(
      { status: "success" },
      ({
        data: {
          me: { fussyAttestationCards },
        },
      }) => (
        <div css={holderStyle}>
          {fussyAttestationCards.map((card, i) => (
            <BentoItemContainer size={{ width: 1, height: 1 }} empty key={i}>
              <FussyAttestation card={card} />
            </BentoItemContainer>
          ))}
        </div>
      )
    )
    .exhaustive();
};

const holderStyle = css`
  display: grid;

  ${mediaQueryIsDesktop(css`
    grid-template-columns: repeat(5, 1fr);
    gap: ${PC_BENTO_ITEM_GAP_SIZE}px;
  `)}

  ${mediaQueryIsMobile(css`
    width: 100%;
    grid-template-columns: repeat(2, 1fr);
  `)}
`;

type Props = {
  card: MeFussyAttestationContentQuery["me"]["fussyAttestationCards"][number];
};

const FussyAttestation = (props: Props) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);

  return (
    <>
      <Body card={props.card} onClick={openModal} />
      <Dialog
        open={isOpen}
        onClose={closeModal}
        fullWidth
        PaperProps={{
          sx: {
            width: "100%",
            height: "80vh",
            m: 1,
          },
        }}
        maxWidth={"lg"}
      >
        <DialogContent>
          <IconButton onClick={closeModal}>
            <CloseIcon sx={{ color: theme.palette.text.primary }} />
          </IconButton>
          <DialogBody card={props.card} />
        </DialogContent>
      </Dialog>
    </>
  );
};

const Body = (props: Props & { onClick: () => void }) => {
  const isSpMode = useIsSpMode();

  return (
    <img
      src={props.card.fussyAttestation.imageUrl}
      alt={`「${props.card.fussyAttestation.fussyAttestationEvent.title}」の認定証`}
      width={isSpMode ? SP_BENTO_WIDTH : PC_BENTO_WIDTH}
      height={isSpMode ? SP_BENTO_HEIGHT : PC_BENTO_HEIGHT}
      css={imageStyle}
      onClick={props.onClick}
    />
  );
};

const DialogBody = (props: Props) => {
  const isSpMode = useIsSpMode();

  return (
    <div>
      <div css={imageContainerStyle}>
        <img
          src={props.card.fussyAttestation.imageUrl}
          alt={`「${props.card.fussyAttestation.fussyAttestationEvent.title}」の認定証`}
          width={isSpMode ? SP_BENTO_WIDTH * 2 : PC_BENTO_WIDTH * 2}
          height={isSpMode ? SP_BENTO_HEIGHT * 2 : PC_BENTO_HEIGHT * 2}
          css={imageStyle}
        />
      </div>
      <div css={attestationEventContainerStyle}>
        <h1>{props.card.fussyAttestation.fussyAttestationEvent.title}</h1>

        <section id={"event-info"}>
          {props.card.fussyAttestation.fussyAttestationEvent.eventAt && (
            <p>
              開催日：
              {formatDate(
                props.card.fussyAttestation.fussyAttestationEvent.eventAt
              )}
            </p>
          )}
          <p>
            発行者：
            {props.card.fussyAttestation.issuers.map((i) => i.name).join(" / ")}
          </p>
        </section>
        <section id={"attestation-info"}>
          <p>
            認証方法：
            {props.card.fussyAttestation.fussyAttestationMethods
              .map((m) => m.name)
              .join(" / ")}
          </p>
          <p>認証日：{formatDate(props.card.issuedAt)}</p>
          <p>
            認証するもの：
            {props.card.fussyAttestation.fussyAttestedAchievement.name}
          </p>
        </section>
      </div>
    </div>
  );
};

const imageContainerStyle = css`
  display: flex;
  justify-content: center;
`;

const imageStyle = css`
  border-radius: 20px;
  margin: -0.25rem;

  ${mediaQueryIsMobile(css`
    width: 100%;
    height: 100%;
    border-radius: 10px;
  `)}
`;

const attestationEventContainerStyle = css`
  ${mediaQueryIsDesktop(css`
    display: grid;
    grid-template-rows: auto 1fr;
    grid-template-columns: 1fr 1fr;
    width: fit-content;
    margin: 0 auto;

    h1 {
      grid-row: 1;
      grid-column: 1 / 3;
      justify-self: center;
    }

    #event-info {
      padding: 0.5rem;
      grid-row: 2 / 3;
      grid-column: 1 / 2;
      justify-self: center;
    }

    #attestation-info {
      padding: 0.5rem;
      grid-row: 2 / 3;
      grid-column: 2 / 3;
      justify-self: center;
    }
  `)};

  ${mediaQueryIsMobile(css`
    h1 {
      text-align: center;
    }
  `)};
`;

export default FussyAttestation;
